import { action, computed, makeObservable } from "mobx";
import { ArrayDataStore } from "Stores/RESTStoreInstances/ArrayDataStore";
import { API_URL } from "General/APIEndpoints";
import { SubmitStore } from "Stores/RESTStoreInstances/SubmitStore";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { formatUsername } from "utils/formatUsername";
import { API } from "General/API";

export class UserDataStore extends ArrayDataStore<API_User> {
  constructor() {
    super();
    this.data = [] as ArrayDataStore<API_User>["data"];
  }
  url(id?: number) {
    if (id) {
      return API_URL.USERS.getUser(id);
    } else {
      return API_URL.USERS.getUsers();
    }
  }

  processResponse = (response) => {
    if (response.items) {
      return response.items;
    } else {
      return [];
    }
  };

  getNameForId(id: number) {
    const user: API_User = this.data.find((val) => val.id === id);
    return user ? formatUsername({ firstName: user.firstName, surname: user.surname }) : "";
  }
}

export const userDataStore = new UserDataStore();

export class UserSubmitStore extends SubmitStore<API_User, API_User, ArrayDataStore<API_User>> {
  private baselineProjectRoles: API_UserProjectRoles[] = [];

  constructor(dataStore) {
    super(dataStore);

    makeObservable(this, {
      userSettings: computed,
      userProjectRoles: computed,
      updateUserSettings: action.bound,
      setProjectGroup: action.bound,
      updateUserRoles: action.bound,
      removeUserRole: action.bound,
      captureProjectRolesBaseline: action.bound,
      isUserIdNew: computed,
      deactivateUser: action.bound,
      activateUser: action.bound,
    });
  }

  clear() {
    super.clear();
    this.baselineProjectRoles = [];
  }

  async fetch(id: string | number) {
    const response = await super.fetch(id);
    this.captureProjectRolesBaseline();
    return response;
  }

  url(id?: number) {
    if (id) {
      return API_URL.USERS.getUser(id);
    } else {
      return API_URL.USERS.getUsers();
    }
  }

  requestPasswordResetByAdmin() {
    const payload: API_RequestPasswordReset = {
      userId: this.data.userId,
      recaptchaResponse: "hui",
      unifiedAccount: false,
    };
    return API.newData(API_URL.PASSWORD.requestPasswordResetByAdmin(), payload);
  }

  get userSettings(): API_UserSettings {
    if (this.data.userSettings) {
      return this.data.userSettings;
    } else {
      return {} as API_UserSettings;
    }
  }

  get userProjectRoles() {
    if (this.data.userSettings && this.data.userSettings.userProjectRoles) {
      return this.data.userSettings.userProjectRoles;
    } else {
      return [];
    }
  }

  captureProjectRolesBaseline() {
    this.baselineProjectRoles = this.userProjectRoles.map((role) => ({ ...role }));
  }

  wasOriginallyUnassigned(projectId: number): boolean {
    const baseline = this.baselineProjectRoles.find((item) => item.projectId === projectId);
    return !baseline?.projectRole || baseline.projectRole === "NONE";
  }

  /**
   * Users cannot be deleted, they only can be deactivated.
   */
  @handleStoreState()
  async deactivateUser() {
    try {
      await this.put(this.data.id, { ...this.data, active: false });
      this.updateData({ active: false });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async activateUser() {
    try {
      await this.put(this.data.id, { ...this.data, active: true });
      this.updateData({ active: true });
    } catch (err) {
      throw err;
    }
  }

  updateUserSettings(o: { [P in keyof API_UserSettings]?: API_UserSettings[P] }) {
    this.data = { ...this.data, userSettings: { ...this.data.userSettings, ...o } };
  }

  setProjectGroup(prGroup?: number) {
    this.data.projectGroupId = prGroup;
  }

  updateUserRoles(userRole: API_UserProjectRoles) {
    // make sure "userProjectRoles" exists
    if (!this.data.userSettings) {
      this.data.userSettings = {} as API_UserSettings;
    }
    if (!this.data.userSettings.userProjectRoles) {
      this.data.userSettings.userProjectRoles = [];
    }
    // check if user has already assigned role in project
    const foundRoleIdx = this.data.userSettings.userProjectRoles.findIndex(
      (item) => item.projectId === userRole.projectId
    );
    if (foundRoleIdx !== -1) {
      this.data.userSettings.userProjectRoles.splice(foundRoleIdx, 1, userRole);
    } else {
      this.data.userSettings.userProjectRoles.push(userRole);
    }
    // Inform user that he has gained new Access Rights
    this.data.userSettings.sendMail = true;
  }

  removeUserRole(projectId: number) {
    if (!this.data.userSettings?.userProjectRoles) {
      return;
    }
    const foundRoleIdx = this.data.userSettings.userProjectRoles.findIndex((item) => item.projectId === projectId);
    if (foundRoleIdx !== -1) {
      this.data.userSettings.userProjectRoles.splice(foundRoleIdx, 1);
    }
  }

  get isUserIdNew() {
    if (this.data.userId && this.data.userId.length) {
      return !this._dataStore.find(this.data.userId, "userId");
    } else {
      return true;
    }
  }

  // action
  // resetForAdditionalUser() {
  //   this.updateData({
  //     id: undefined,
  //     userId: '',
  //     name: '',
  //     phoneNumber: '',
  //     companyName: '',
  //     fax: '',
  //     active: true,
  //     companyRole: '',
  //     customerId: '',
  //     street: '',
  //     mobileNumber: '',
  //     email: '',
  //     email2: '',
  //     city: '',
  //     postalCode: '',
  //     projectGroupId: undefined
  //   });
  // }
}

export const userSubmitStore = new UserSubmitStore(userDataStore);
